import Lucide from "src/base-components/Lucide";
import EmojiPicker from "src/components/EmojiPicker";
import MessageReactionPicker from "src/components/MessageReactionPicker";
import MessageTicks from "./MessageStatus";
import { ChatMessage, ChatParticipant, ReplyTo } from "src/pubnub";
import { useGroupChat } from "src/pubnub/useGroupChat";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePubNub } from "pubnub-react";
import { ChatUser } from "../index";
import ScrollToBottomButton from "./ScrollToBottomButton";
import ActionModal from "./ActionModal";
import ChatMessageInput from "./ChatMessageInput";
import MessageActionMenu from "./MessageActionMenu";
import LazyFileAttachment from "./LazyFileAttachment";
import SlideoverModal from "../../SlideoverModal";
import Button from "src/base-components/Button";
import TestImage from "../../../assets/images/TestImages/user.png";
import { updateGroupService, fetchChatUsersService } from "src/services/user.services";
import { useAppDispatch } from "src/stores";
import { setAlert } from "src/stores/basicSlices";
import { DateTime } from 'luxon';

/* ---------- props ---------- */

interface GroupChatPanelProps {
  currentUserId: string;
  currentUserName: string;
  groupId: string;
  groupName: string;
  members: ChatParticipant[];
  channelId: string; // actual PubNub channel from API
  onBack?: () => void;
  availableUsers?: ChatUser[];
  onMembersChange?: (members: ChatParticipant[]) => void;
  onGroupNameChange?: (newName: string) => void;
  canManageGroup?: boolean; // permission to add/remove members & edit group name
  isRemoved?: boolean; // true when user was removed from the group (read-only mode)
  readOnly?: boolean; // true when viewing from Ongoing Chats tab (view-only mode)
}

/* ---------- constants ---------- */

// VIDEO UPLOAD DISABLED: removed video/* from allowed types
const ALLOWED_FILE_TYPES = "image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt";
const MAX_FILE_SIZE_MB = 4;
const MAX_FILES_PER_MESSAGE = 10;

/** Shape for each selected file with its preview URL */
interface SelectedFileItem {
  file: File;
  preview: string | null; // video upload disabled — only images get previews
  id: string;
}

/* ---------- component ---------- */

const GroupChatPanel: React.FC<GroupChatPanelProps> = ({
  currentUserId,
  currentUserName,
  groupId,
  groupName,
  members,
  channelId,
  onBack,
  availableUsers = [],
  onMembersChange,
  onGroupNameChange,
  canManageGroup = false,
  isRemoved = false,
  readOnly = false,
}) => {
  const pubnub = usePubNub();
  const dispatch = useAppDispatch();

  // Derive active members (not left) and current user's left_at timestamp
  const activeMembers = useMemo(
    () => members.filter(m => !m.leftAt),
    [members]
  );
  // const currentUserLeftAt = useMemo(() => {
  //   const entry = members.find(m => m.id === currentUserId);
  //   return entry?.leftAt ? new Date(entry.leftAt).getTime() : null;
  // }, [members, currentUserId]);

  const currentUserLeftAt = useMemo(() => {
    const entry = members.find(m => m.id === currentUserId);
    if (!entry?.leftAt) return null;

    // Parse the backend string as Calgary time (Canada/Mountain)
    const dt = DateTime.fromSQL(entry.leftAt, { zone: 'America/Edmonton' });

    // Convert to UTC milliseconds to match PubNub timestamps
    return dt.toMillis();
  }, [members, currentUserId]);

  /* ---- local state ---- */
  const [inputMessage, setInputMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<SelectedFileItem[]>([]);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ReplyTo | null>(null);

  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; message: ChatMessage | null }>({
    isOpen: false,
    message: null,
  });
  const [actionMenuState, setActionMenuState] = useState<{
    isOpen: boolean;
    message: ChatMessage | null;
    position: { x: number; y: number };
  }>({ isOpen: false, message: null, position: { x: 0, y: 0 } });
  const [reactionPickerState, setReactionPickerState] = useState<{
    isOpen: boolean;
    messageTimetoken: string | null;
    position: { x: number; y: number };
  }>({ isOpen: false, messageTimetoken: null, position: { x: 0, y: 0 } });
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
  const [showMembersList, setShowMembersList] = useState(false);

  /* ---- Member management ---- */
  const [localMembers, setLocalMembers] = useState<ChatParticipant[]>(members);
  const [showAddMember, setShowAddMember] = useState(false);
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  const [selectedAddMembers, setSelectedAddMembers] = useState<{ id: string; name: string }[]>([]);

  /* ---- Add member: own API fetch with debounce ---- */
  const [addMemberUsers, setAddMemberUsers] = useState<ChatUser[]>([]);
  const [addMemberLoading, setAddMemberLoading] = useState(false);
  const addMemberDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  /* ---- Edit group name ---- */
  const [isEditingGroupName, setIsEditingGroupName] = useState(false);
  const [editGroupNameValue, setEditGroupNameValue] = useState(groupName);
  const [editGroupNameLoading, setEditGroupNameLoading] = useState(false);
  const groupNameInputRef = useRef<HTMLInputElement>(null);

  // Sync local members (active only) when switching between groups
  useEffect(() => {
    setLocalMembers(activeMembers);
    setShowAddMember(false);
    setAddMemberSearch("");
    setAddMemberUsers([]);
    setSelectedAddMembers([]);
    setIsEditingGroupName(false);
    setEditGroupNameValue(groupName);
  }, [groupId]);

  // Sync local members when members prop changes (e.g. member removed/added by another user)
  useEffect(() => {
    setLocalMembers(activeMembers);
  }, [activeMembers]);

  // Sync group name when prop changes
  useEffect(() => {
    setEditGroupNameValue(groupName);
  }, [groupName]);

  // Auto-focus the group name input when editing starts
  useEffect(() => {
    if (isEditingGroupName) {
      setTimeout(() => groupNameInputRef.current?.focus(), 50);
    }
  }, [isEditingGroupName]);

  // Fetch users from API when Add Member opens or search changes
  const fetchAddMemberUsers = useCallback(async (search: string) => {
    setAddMemberLoading(true);
    try {
      const res: any = await fetchChatUsersService(1, search);
      const pageData = res?.data || res || [];
      const users: ChatUser[] = Array.isArray(pageData) ? pageData : pageData?.data || [];
      setAddMemberUsers(users);
    } catch (err) {
      console.error("Failed to fetch users for add member:", err);
    } finally {
      setAddMemberLoading(false);
    }
  }, []);

  // Load users when Add Member panel opens
  useEffect(() => {
    if (showAddMember) {
      setAddMemberSearch("");
      setSelectedAddMembers([]);
      fetchAddMemberUsers("");
    }
    return () => { if (addMemberDebounceRef.current) clearTimeout(addMemberDebounceRef.current); };
  }, [showAddMember]);

  // Debounced search for add member
  const handleAddMemberSearchChange = (value: string) => {
    setAddMemberSearch(value);
    if (addMemberDebounceRef.current) clearTimeout(addMemberDebounceRef.current);
    addMemberDebounceRef.current = setTimeout(() => fetchAddMemberUsers(value), 400);
  };

  // Filter out existing members + self + already-selected from API results
  const memberIds = new Set(localMembers.map((m) => m.id));
  const selectedAddIds = new Set(selectedAddMembers.map((u) => u.id));
  const filteredAvailableUsers = addMemberUsers.filter(
    (u) => !memberIds.has(String(u.id)) && !selectedAddIds.has(String(u.id)) && String(u.id) !== currentUserId
  );

  // Toggle user selection in Add Member modal
  const toggleSelectAddMember = (user: ChatUser) => {
    const uid = String(user.id);
    const fullName = `${user.name}${user.last_name ? " " + user.last_name : ""}`;
    setSelectedAddMembers((prev) =>
      prev.some((u) => u.id === uid)
        ? prev.filter((u) => u.id !== uid)
        : [...prev, { id: uid, name: fullName }]
    );
  };

  // Batch add all selected members in one API call
  const handleAddMembers = async () => {
    if (memberActionLoading || selectedAddMembers.length === 0) return;
    setMemberActionLoading(true);
    try {
      const userIds = selectedAddMembers.map((u) => Number(u.id));
      await updateGroupService(channelId, {
        user_ids: userIds,
        action: "add",
      });

      const newMembers: ChatParticipant[] = selectedAddMembers.map((u) => ({ id: u.id, name: u.name }));
      const updated = [...localMembers, ...newMembers];
      setLocalMembers(updated);
      onMembersChange?.(updated);

      // Notify each added member via their system channel
      selectedAddMembers.forEach((user) => {
        pubnub.publish({
          channel: `chat-system-${user.id}`,
          message: {
            type: "member-added",
            groupId,
            channelId,
            groupName,
            members: updated,
            addedBy: currentUserName,
          },
          storeInHistory: true,
        }).catch((err) => console.warn("[System] Failed to notify added member:", err));
      });

      // Publish one system message inside the group channel
      const names = selectedAddMembers.map((u) => u.name).join(", ");
      pubnub.publish({
        channel: channelId,
        message: {
          id: `sys-${Date.now()}`,
          text: `${currentUserName} added ${names}`,
          senderId: currentUserId,
          senderName: currentUserName,
          timestamp: Date.now(),
          channelId,
          type: "system",
        },
        storeInHistory: true,
      }).catch(() => { });

      // Close modal and reset
      setShowAddMember(false);
      setSelectedAddMembers([]);
      setAddMemberSearch("");
    } catch (err) {
      console.error("Failed to add members:", err);
    } finally {
      setMemberActionLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (memberId === currentUserId || memberActionLoading) return;
    const removedMember = localMembers.find((m) => m.id === memberId);
    setMemberActionLoading(true);
    try {
      await updateGroupService(channelId, {
        user_ids: [Number(memberId)],
        action: "remove",
      });
      const removedAt = new Date().toISOString();
      const updated = localMembers.filter((m) => m.id !== memberId);
      setLocalMembers(updated);
      onMembersChange?.(updated);

      // Notify removed member via their system channel.
      // Include the removed member with leftAt so their client can filter messages.
      const memberSystemChannel = `chat-system-${memberId}`;
      pubnub.publish({
        channel: memberSystemChannel,
        message: {
          type: "member-removed",
          groupId,
          channelId,
          removedBy: currentUserName,
          removedAt,
          members: updated,
        },
        storeInHistory: true,
      }).catch((err) => console.warn("[System] Failed to notify removed member:", err));

      // Notify ALL remaining members (except self) so their member list & online status update
      updated.forEach((member) => {
        if (member.id === currentUserId) return;
        pubnub.publish({
          channel: `chat-system-${member.id}`,
          message: {
            type: "member-list-updated",
            groupId,
            channelId,
            members: updated,
            updatedBy: currentUserName,
          },
          storeInHistory: true,
        }).catch(() => { });
      });

      // Publish system message inside the group channel
      pubnub.publish({
        channel: channelId,
        message: {
          id: `sys-${Date.now()}`,
          text: `${currentUserName} removed ${removedMember?.name || "a member"}`,
          senderId: currentUserId,
          senderName: currentUserName,
          timestamp: Date.now(),
          channelId,
          type: "system",
        },
        storeInHistory: true,
      }).catch(() => { });
    } catch (err) {
      console.error("Failed to remove member:", err);
    } finally {
      setMemberActionLoading(false);
    }
  };

  const handleSaveGroupName = async () => {
    const trimmed = editGroupNameValue.trim();
    if (!trimmed || trimmed === groupName || editGroupNameLoading) return;
    setEditGroupNameLoading(true);
    try {
      await updateGroupService(channelId, { group_name: trimmed });
      onGroupNameChange?.(trimmed);
      setIsEditingGroupName(false);

      // Notify all other members about the rename via their system channels
      localMembers.forEach((member) => {
        if (member.id === currentUserId) return;
        const memberSystemChannel = `chat-system-${member.id}`;
        pubnub.publish({
          channel: memberSystemChannel,
          message: {
            type: "group-renamed",
            groupId,
            channelId,
            newName: trimmed,
            renamedBy: currentUserName,
          },
          storeInHistory: true,
        }).catch(() => { });
      });

      // Publish system message inside the group channel
      pubnub.publish({
        channel: channelId,
        message: {
          id: `sys-${Date.now()}`,
          text: `${currentUserName} changed the group name to "${trimmed}"`,
          senderId: currentUserId,
          senderName: currentUserName,
          timestamp: Date.now(),
          channelId,
          type: "system",
        },
        storeInHistory: true,
      }).catch(() => { });
    } catch (err) {
      console.error("Failed to update group name:", err);
    } finally {
      setEditGroupNameLoading(false);
    }
  };

  const handleCancelEditGroupName = () => {
    setEditGroupNameValue(groupName);
    setIsEditingGroupName(false);
  };

  const toggleExpandMessage = (id: string) =>
    setExpandedMessages((prev) => ({ ...prev, [id]: !prev[id] }));

  /* ---- scroll-to-reply highlight ---- */
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [isScrollingToReply, setIsScrollingToReply] = useState(false);

  /* ---- scroll-to-bottom button (WhatsApp style) ---- */
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  /* ---- floating date label (WhatsApp style) ---- */
  const [floatingDateLabel, setFloatingDateLabel] = useState("");
  const [isFloatingDateVisible, setIsFloatingDateVisible] = useState(false);
  const scrollHideTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Cleanup floating date timer on unmount
  useEffect(() => {
    return () => {
      if (scrollHideTimerRef.current) clearTimeout(scrollHideTimerRef.current);
    };
  }, []);

  /* ---- refs ---- */
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeightRef = useRef<number>(0);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /* ---- auto-resize textarea ---- */
  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`; // max ~5 rows
  };

  useEffect(() => {
    autoResize();
  }, [inputMessage]);

  /* ---- hook ---- */
  const {
    messages,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    isConnected,
    onlineMembers,
    onlineCount,
    typingUsers,
    reactions,
    deletedForMe,
    sendMessage,
    editMessage,
    deleteMessage,
    sendTypingIndicator,
    addReaction,
    removeReaction,
    markAsRead,
    getMessageStatus,
    loadMoreMessages,
    loadMessagesToTimetoken,
  } = useGroupChat({
    currentUserId,
    currentUserName,
    groupId,
    members: activeMembers,
    channelId,
    autoSubscribe: true,
    historyCount: 25,
    isRemoved,
  });

  // If the current user has left / was removed, only show messages up to their left_at time.
  // Use isRemoved prop as a fallback — if isRemoved is true but leftAt is missing
  // (e.g. stale state during rapid add/remove cycles), freeze at the moment isRemoved turned true.
  const removedAtFallbackRef = useRef<number | null>(null);
  useEffect(() => {
    if (isRemoved && !currentUserLeftAt) {
      // Set a fresh timestamp each time user is removed (handles rapid add/remove)
      removedAtFallbackRef.current = Date.now();
    } else if (!isRemoved) {
      removedAtFallbackRef.current = null;
    }
  }, [isRemoved, currentUserLeftAt]);
  const effectiveLeftAt = currentUserLeftAt ?? (isRemoved ? removedAtFallbackRef.current : null);

  const visibleMessages = useMemo(() => {
    if (!effectiveLeftAt) return messages;
    return messages.filter(msg => {
      const msgTime = msg.timestamp ?? 0;
      return msgTime <= effectiveLeftAt;
    });
  }, [messages, effectiveLeftAt]);


  /* ---- scroll helpers ---- */
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (isInitialLoadRef.current && messages.length > 0 && !isLoading) {
      isInitialLoadRef.current = false;
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "auto" }), 50);
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isLoadingMore && messagesContainerRef.current && prevScrollHeightRef.current > 0) {
      const c = messagesContainerRef.current;
      const diff = c.scrollHeight - prevScrollHeightRef.current;
      if (diff > 0) c.scrollTop = diff;
      prevScrollHeightRef.current = 0;
    }
  }, [isLoadingMore, messages]);

  const handleScroll = useCallback(() => {
    const c = messagesContainerRef.current;
    if (!c) return;

    // Show/hide scroll-to-bottom button
    const distanceFromBottom = c.scrollHeight - c.scrollTop - c.clientHeight;
    setShowScrollToBottom(distanceFromBottom > 100);

    // Floating date label — find the last date separator scrolled past the container top
    const separators = c.querySelectorAll<HTMLElement>("[data-date-label]");
    const containerTop = c.getBoundingClientRect().top;
    let currentLabel = "";
    separators.forEach((el) => {
      if (el.getBoundingClientRect().top <= containerTop + 20) {
        currentLabel = el.getAttribute("data-date-label") || "";
      }
    });
    // If no separator scrolled past, use the first visible one
    if (!currentLabel && separators.length > 0) {
      currentLabel = separators[0].getAttribute("data-date-label") || "";
    }
    if (currentLabel) {
      setFloatingDateLabel(currentLabel);
      setIsFloatingDateVisible(true);
    }
    // Auto-hide after 1.5s of no scrolling
    if (scrollHideTimerRef.current) clearTimeout(scrollHideTimerRef.current);
    scrollHideTimerRef.current = setTimeout(() => setIsFloatingDateVisible(false), 1500);

    if (c.scrollTop < 100 && hasMore && !isLoadingMore && !isLoading) {
      prevScrollHeightRef.current = c.scrollHeight;
      loadMoreMessages();
    }
  }, [hasMore, isLoadingMore, isLoading, loadMoreMessages]);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Mark incoming messages as read — only the latest unread message needs a read receipt.
  // PubNub read receipts are per-message, but we only need to mark the newest one
  // since "read up to X" implies all prior messages are read too.
  const lastReadTimetokenRef = useRef<string | null>(null);
  useEffect(() => {
    if (isRemoved || readOnly) return;
    // Find the last message from another user
    for (let i = visibleMessages.length - 1; i >= 0; i--) {
      const msg = visibleMessages[i];
      if (msg.senderId !== currentUserId && msg.timetoken) {
        const tt = String(msg.timetoken);
        if (tt !== lastReadTimetokenRef.current) {
          lastReadTimetokenRef.current = tt;
          markAsRead(tt);
        }
        break;
      }
    }
  }, [visibleMessages, currentUserId, markAsRead, isRemoved, readOnly]);

  /* ---- handlers ---- */

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
    sendTypingIndicator(true);
  };

  // Format time
  const formatTime = (ts: number) =>
    new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });

  // Date helpers (same as OneToOneChatPanel)
  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

  // const getDateLabel = (ts: number): string => {
  //   const d = new Date(ts);
  //   const today = new Date();
  //   if (isSameDay(d, today)) return "Today";
  //   const yest = new Date();
  //   yest.setDate(today.getDate() - 1);
  //   if (isSameDay(d, yest)) return "Yesterday";
  //   return d.toLocaleDateString(
  //     undefined,
  //     d.getFullYear() === today.getFullYear()
  //       ? { day: "2-digit", month: "short" }
  //       : { day: "2-digit", month: "short", year: "numeric" },
  //   );
  // };

  const getDateLabel = (ts: number): string => {
    const d = new Date(ts);
    const today = new Date();
    const tz = "America/Edmonton"; // Canada Mountain Time

    const isSameDay = (a: Date, b: Date) =>
      a.toLocaleDateString("en-CA", { timeZone: tz }) ===
      b.toLocaleDateString("en-CA", { timeZone: tz });

    if (isSameDay(d, today)) return "Today";

    const yest = new Date(today);
    yest.setDate(today.getDate() - 1);

    if (isSameDay(d, yest)) return "Yesterday";

    return d.toLocaleDateString(
      "en-CA",
      d.getFullYear() === today.getFullYear()
        ? { day: "2-digit", month: "short", timeZone: tz }
        : { day: "2-digit", month: "short", year: "numeric", timeZone: tz },
    );
  };

  // File handling — multi-file
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;

    const incoming = Array.from(fileList);
    const totalAfterAdd = selectedFiles.length + incoming.length;

    if (totalAfterAdd > MAX_FILES_PER_MESSAGE) {
      dispatch(setAlert({
        text: `You can attach up to ${MAX_FILES_PER_MESSAGE} files per message.`, alertType: "Swal",
        swalIcon: "error",
      }));
      e.target.value = "";
      return;
    }

    const oversized = incoming.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (oversized.length > 0) {
      dispatch(setAlert({
        text: `File size must be less than ${MAX_FILE_SIZE_MB}MB. Skipped: ${oversized.map((f) => f.name).join(", ")}`,
        alertType: "Swal",
        swalIcon: "error",
        // icon: "XCircle",
      }));
    }

    const validFiles = incoming.filter((f) => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024);
    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }

    // VIDEO UPLOAD DISABLED: reject any video files
    const nonVideoFiles = validFiles.filter((f) => !f.type.startsWith("video/"));
    if (nonVideoFiles.length < validFiles.length) {
      dispatch(setAlert({
        text: "Video uploads are currently disabled.",
        alertType: "Swal",
        swalIcon: "error",
      }));
    }
    if (nonVideoFiles.length === 0) {
      e.target.value = "";
      return;
    }

    const newItems: SelectedFileItem[] = nonVideoFiles.map((file) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      return { file, preview: null, id };
    });

    // Generate previews asynchronously for images
    // VIDEO UPLOAD DISABLED: removed video preview generation
    newItems.forEach((item) => {
      if (item.file.type.startsWith("image/") /* || item.file.type.startsWith("video/") */) {
        const reader = new FileReader();
        reader.onload = (ev) => {
          setSelectedFiles((prev) =>
            prev.map((f) =>
              f.id === item.id ? { ...f, preview: ev.target?.result as string } : f,
            ),
          );
        };
        reader.readAsDataURL(item.file);
      }
    });

    setSelectedFiles((prev) => [...prev, ...newItems]);
    e.target.value = "";
  };

  /** Remove a single file by id, or clear all */
  const handleRemoveFile = (fileId?: string) => {
    if (fileId) {
      setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
    } else {
      setSelectedFiles([]);
    }
  };

  const getFileIcon = (ft: string) => {
    if (ft?.startsWith("image/")) return "Image";
    // VIDEO UPLOAD DISABLED: video icon mapping commented out
    // if (ft?.startsWith("video/")) return "Video";
    if (ft?.includes("pdf")) return "FileText";
    if (ft?.includes("word") || ft?.includes("document")) return "FileText";
    if (ft?.includes("excel") || ft?.includes("sheet")) return "FileSpreadsheet";
    return "File";
  };

  // Send
  const handleSendWithFile = async () => {
    if (isSending) return;

    const hasText = inputMessage.trim().length > 0;
    const hasFiles = selectedFiles.length > 0;
    if (!hasText && !hasFiles) return;

    setIsSending(true);
    try {
      const messageText = inputMessage.trim();
      const rawFiles = hasFiles ? selectedFiles.map((f) => f.file) : undefined;

      await sendMessage(messageText, rawFiles, replyingTo || undefined);

      handleRemoveFile(); // clears all files
      setInputMessage("");
      setReplyingTo(null);
      sendTypingIndicator(false);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err: any) {
      console.error("Failed to send message:", err);
    } finally {
      setIsSending(false);
    }
  };

  // Reply — include file context when message has attachments
  const handleReplyToMessage = (msg: ChatMessage) => {
    const msgFiles = msg.files?.length ? msg.files : msg.file ? [msg.file] : [];
    let replyText = msg.text || "";
    if (msgFiles.length > 0 && !replyText) {
      const imageCount = msgFiles.filter((f) => f.type?.startsWith("image/")).length;
      // VIDEO UPLOAD DISABLED: video count commented out
      // const videoCount = msgFiles.filter((f) => f.type?.startsWith("video/")).length;
      const docCount = msgFiles.length - imageCount;
      const parts: string[] = [];
      if (imageCount) parts.push(`${imageCount} image${imageCount > 1 ? "s" : ""}`);
      // if (videoCount) parts.push(`${videoCount} video${videoCount > 1 ? "s" : ""}`);
      if (docCount) parts.push(`${docCount} file${docCount > 1 ? "s" : ""}`);
      replyText = `📎 ${parts.join(", ")}`;
    } else if (msgFiles.length > 0 && replyText) {
      replyText = `📎 ${replyText}`;
    }
    if (replyText.length > 100) replyText = replyText.substring(0, 100) + "...";

    setReplyingTo({
      messageId: msg.id,
      text: replyText,
      senderId: msg.senderId,
      senderName: msg.senderName,
      timetoken: msg.timetoken,
    });
  };
  const cancelReply = () => setReplyingTo(null);

  // Scroll to the original replied message (WhatsApp style)
  // If the message isn’t loaded yet, fetch older pages until it appears.
  const scrollToMessage = useCallback(
    async (messageId: string, timetoken?: string | number) => {

      // 1. Already in DOM?
      const el = messageRefs.current.get(messageId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setHighlightedMessageId(messageId);
        setTimeout(() => setHighlightedMessageId(null), 2000);
        return;
      }

      // 2. Resolve timetoken - if not provided, look it up from loaded messages
      let resolvedTimetoken = timetoken ? String(timetoken) : undefined;
      if (!resolvedTimetoken) {
        const match = visibleMessages.find((m) => m.id === messageId);
        if (match?.timetoken) {
          resolvedTimetoken = String(match.timetoken);
        }
      }

      if (!resolvedTimetoken) {
        return;
      }

      // 3. Not loaded - fetch until we find it
      setIsScrollingToReply(true);
      const found = await loadMessagesToTimetoken(resolvedTimetoken);
      setIsScrollingToReply(false);

      if (found) {
        // Wait for React to render the newly-loaded messages
        await new Promise((r) => setTimeout(r, 300));
        const target = messageRefs.current.get(messageId);
        if (target) {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
          setHighlightedMessageId(messageId);
          setTimeout(() => setHighlightedMessageId(null), 2000);
        }
      }
    },
    [loadMessagesToTimetoken, visibleMessages],
  );

  // Emoji
  const handleEmojiSelect = (emoji: string) => setInputMessage((p) => p + emoji);

  // Action menu
  const handleShowActionMenu = (e: React.MouseEvent, msg: ChatMessage) => {
    e.preventDefault();
    e.stopPropagation();
    setActionMenuState({ isOpen: true, message: msg, position: { x: e.clientX, y: e.clientY } });
  };
  const closeActionMenu = () =>
    setActionMenuState({ isOpen: false, message: null, position: { x: 0, y: 0 } });

  // Edit
  const handleStartEdit = (msg: ChatMessage) => {
    setEditingMessage(msg);
    setInputMessage(msg.text);
    closeActionMenu();
  };
  const cancelEdit = () => {
    setEditingMessage(null);
    setInputMessage("");
  };
  const handleSaveEdit = async () => {
    if (!editingMessage || !inputMessage.trim()) return;
    await editMessage(editingMessage.id, inputMessage);
    setEditingMessage(null);
    setInputMessage("");
  };

  // Delete
  const handleShowDeleteModal = (msg: ChatMessage) => {
    setDeleteModal({ isOpen: true, message: msg });
    closeActionMenu();
  };
  const handleDelete = (forEveryone: boolean) => {
    if (deleteModal.message) deleteMessage(deleteModal.message.id, forEveryone);
    setDeleteModal({ isOpen: false, message: null });
  };

  // Reaction picker (right-click)
  const handleMessageContextMenu = (e: React.MouseEvent, tt: string) => {
    e.preventDefault();
    setReactionPickerState({ isOpen: true, messageTimetoken: tt, position: { x: e.clientX, y: e.clientY } });
  };
  const handleAddReaction = async (emoji: string) => {
    if (!reactionPickerState.messageTimetoken) return;
    await addReaction(reactionPickerState.messageTimetoken, emoji);
  };
  const closeReactionPicker = () =>
    setReactionPickerState({ isOpen: false, messageTimetoken: null, position: { x: 0, y: 0 } });

  /* ---- build typing text (WhatsApp style) ---- */
  const buildTypingText = (): string | null => {
    if (typingUsers.length === 0) return null;
    if (typingUsers.length === 1) return `${typingUsers[0].userName} is typing...`;
    if (typingUsers.length === 2) return `${typingUsers[0].userName}, ${typingUsers[1].userName} are typing...`;
    return `${typingUsers[0].userName}, ${typingUsers[1].userName} and ${typingUsers.length - 2} more are typing...`;
  };

  const typingText = buildTypingText();

  /* ---- helper: get sender colour seed ---- */
  const senderColors = [
    "#4C1D95", "#9F1239", "#0369A1", "#15803D", "#B45309", "#7C3AED", "#0D9488", "#DB2777",
  ];
  const getSenderColor = (senderId: string) => {
    let hash = 0;
    for (let i = 0; i < senderId.length; i++) hash = senderId.charCodeAt(i) + ((hash << 5) - hash);
    return senderColors[Math.abs(hash) % senderColors.length];
  };

  return (
    <div className="flex flex-col flex-1 h-full min-w-0">
      {/* ---- Header ---- */}
      <div className="flex items-center gap-3 border-b p-4 bg-white">
        {/* Group avatar */}
        <div className="w-10 h-10 rounded-full bg-[linear-gradient(15deg,_rgb(76,29,149)_15%,_rgb(159,18,57)_90%)] opacity-75 flex items-center justify-center text-white font-bold text-lg">
          {groupName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          {canManageGroup && !readOnly && isEditingGroupName ? (
            <div className="flex items-center gap-1.5">
              <input
                ref={groupNameInputRef}
                type="text"
                value={editGroupNameValue}
                onChange={(e) => setEditGroupNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveGroupName();
                  if (e.key === "Escape") handleCancelEditGroupName();
                }}
                className="flex-1 min-w-0 px-2 py-0.5 text-sm font-medium border rounded-md focus:outline-none focus:ring-1 focus:ring-purple-400 bg-slate-50"
                disabled={editGroupNameLoading}
              />
              <button
                onClick={handleSaveGroupName}
                disabled={editGroupNameLoading || !editGroupNameValue.trim() || editGroupNameValue.trim() === groupName}
                className="p-1 rounded-full hover:bg-green-50 text-green-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Save"
              >
                {editGroupNameLoading ? (
                  <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
                ) : (
                  <Lucide icon="Check" className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleCancelEditGroupName}
                disabled={editGroupNameLoading}
                className="p-1 rounded-full hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                title="Cancel"
              >
                <Lucide icon="X" className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 group/name">
              <div className="font-medium truncate">{groupName}</div>
              {canManageGroup && !readOnly && (
                <button
                  onClick={() => setIsEditingGroupName(true)}
                  className="p-0.5 rounded opacity-0 group-hover/name:opacity-100 hover:bg-slate-100 text-slate-400 hover:text-purple-600 transition-all"
                  title="Edit group name"
                >
                  <Lucide icon="Pencil" className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          )}
          <div className="text-xs text-slate-500">
            {typingText ? (
              <span className="text-green-600 italic">{typingText}</span>
            ) : (
              <button
                onClick={() => setShowMembersList((prev) => !prev)}
                className="hover:text-purple-600 transition-colors cursor-pointer"
              >
                {/* {localMembers.length} members{onlineCount > 0 ? `, ${onlineCount} online` : ""} */}
                {localMembers.length} {localMembers.length === 1 ? "member" : "members"}
                {onlineCount > 0 ? `, ${onlineCount} online` : ""}
                <Lucide
                  icon={showMembersList ? "ChevronUp" : "ChevronDown"}
                  className="w-3 h-3 inline ml-1"
                />
              </button>
            )}
          </div>
        </div>
        {!isConnected && (
          <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">Connecting...</span>
        )}
      </div>

      {/* ---- Members List (toggleable, fixed height with scroll) ---- */}
      {showMembersList && (
        <div className="border-b bg-white px-4 py-2">
          {/* Add member button */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-500">
              {localMembers.length} members
            </span>
            {canManageGroup && !readOnly && (
              <button
                onClick={() => setShowAddMember(true)}
                className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 font-medium"
              >
                <Lucide icon="UserPlus" className="w-3.5 h-3.5" />
                Add Member
              </button>
            )}
          </div>

          {/* Current members list */}
          <div className="max-h-48 overflow-y-auto space-y-1">
            {localMembers.map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 group"
              >
                <div className="relative flex-shrink-0">
                  <div className="w-7 h-7 rounded-full bg-[linear-gradient(15deg,_rgb(76,29,149)_15%,_rgb(159,18,57)_90%)] opacity-75 flex items-center justify-center text-white text-xs font-semibold">
                    {member.name.charAt(0).toUpperCase()}
                  </div>
                  {onlineMembers.includes(member.id) && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                  )}
                </div>
                <span className="text-sm text-slate-700 truncate flex-1">
                  {member.name}
                  {member.id === currentUserId && (
                    <span className="text-xs text-slate-400 ml-1">(You)</span>
                  )}
                </span>
                {canManageGroup && !readOnly && member.id !== currentUserId && (
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    disabled={memberActionLoading}
                    className=" p-1 rounded-full text-red-500 transition-all"
                    title="Remove member"
                  >
                    <Lucide icon="X" className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- Add Member SlideoverModal ---- */}
      {canManageGroup && (
        <SlideoverModal
          size="md"
          visible={showAddMember}
          onClose={() => setShowAddMember(false)}
          headerLeft={
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[linear-gradient(135deg,#4C1D95_0%,#9F1239_100%)] flex items-center justify-center">
                <Lucide icon="UserPlus" className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-[16px] text-[#1D293D] font-bold">Add Members</h2>
            </div>
          }
        >
          <div className="flex flex-col h-full">
            <div className="flex-1 space-y-5">
              {/* Selected Members Chips */}
              {selectedAddMembers.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedAddMembers.map((u) => (
                    <span
                      key={u.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                    >
                      <img src={TestImage} className="w-4 h-4 rounded-full" alt="" />
                      {u.name}
                      <button onClick={() => setSelectedAddMembers((prev) => prev.filter((m) => m.id !== u.id))} className="hover:text-red-500">
                        <Lucide icon="X" className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Search */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Search Users ({selectedAddMembers.length} selected)
                </label>
                <div className="relative mb-2">
                  <Lucide
                    icon="Search"
                    className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="text"
                    placeholder="Search users to add..."
                    value={addMemberSearch}
                    onChange={(e) => handleAddMemberSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-0 focus:border-slate-200 bg-slate-50"
                    autoFocus
                  />
                </div>

                {/* User List */}
                <div className="max-h-[calc(115vh-430px)] overflow-y-auto space-y-1 rounded-lg p-1">
                  {addMemberLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Lucide icon="Loader2" className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                  ) : filteredAvailableUsers.length === 0 ? (
                    <p className="text-center text-sm text-slate-400 py-8">No users found</p>
                  ) : (
                    filteredAvailableUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => toggleSelectAddMember(user)}
                        className="flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition hover:bg-slate-50"
                      >
                        <img src={TestImage} className="w-8 h-8 rounded-full" alt="" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{user.name}{user.last_name ? ` ${user.last_name}` : ""}</p>
                          <p className="text-xs text-slate-400">{user.email}</p>
                        </div>
                        <Lucide icon="Plus" className="w-4 h-4 text-purple-600" />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t mt-4 flex items-center justify-center gap-3">
              <Button
                variant="outline-secondary"
                onClick={() => setShowAddMember(false)}
                className="px-4 py-2 h-[38px] text-[12px]"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAddMembers}
                disabled={selectedAddMembers.length === 0 || memberActionLoading}
                className="px-6 py-2 h-[38px] text-[12px] bg-[linear-gradient(135deg,#4C1D95_0%,#9F1239_100%)]"
              >
                {memberActionLoading ? (
                  <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Lucide icon="UserPlus" className="w-4 h-4 mr-2" />
                )}
                Add {selectedAddMembers.length > 0 ? `(${selectedAddMembers.length})` : "Members"}
              </Button>
            </div>
          </div>
        </SlideoverModal>
      )}

      {/* ---- Error Banner ---- */}
      {error && (
        <div className="bg-red-50 text-red-600 text-sm px-4 py-2 border-b border-red-100">{error}</div>
      )}

      {/* ---- Messages ---- */}
      <div className="relative flex-1 overflow-hidden">
        {/* Floating date pill — WhatsApp style: shows on scroll, fades after idle */}
        <div
          className={`absolute top-2 left-1/2 -translate-x-1/2 z-30 pointer-events-none transition-opacity duration-300 ${isFloatingDateVisible && floatingDateLabel ? "opacity-100" : "opacity-0"
            }`}
        >
          <span className="text-[11px] text-slate-500 bg-white px-3 py-1 rounded-full shadow-md">
            {floatingDateLabel}
          </span>
        </div>

        {isScrollingToReply && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <div className="flex items-center gap-2 rounded-full bg-white px-4 py-2 shadow-lg">
              <Lucide icon="Loader2" className="w-5 h-5 animate-spin text-purple-600" />
              <span className="text-sm text-gray-600">Loading messages…</span>
            </div>
          </div>
        )}
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="h-full p-4 overflow-y-auto overflow-x-hidden space-y-3 bg-[#F7F1F5]"
        >
          {isLoadingMore && (
            <div className="flex justify-center py-2">
              <Lucide icon="Loader2" className="w-5 h-5 animate-spin text-purple-600" />
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <Lucide icon="Loader2" className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : visibleMessages.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-full text-slate-400">
              <Lucide icon="Users" className="w-12 h-12 mb-2" />
              <p>No messages yet. Say hello to the group!</p>
            </div>
          ) : (
            visibleMessages.map((msg: any, idx: number) => {
              if (deletedForMe.has(msg.id)) return null;

              const isMine = msg.senderId === currentUserId;
              const messageTimetoken = msg.timetoken;
              const msgReactions = messageTimetoken ? reactions[messageTimetoken] || [] : [];
              const canReact = !!messageTimetoken && !msg.isDeleted;
              const isExpanded = !!expandedMessages[String(msg.id)];
              const shouldTruncate = typeof msg.text === "string" && msg.text.length > 100;

              // Collect all files for this message
              const msgFiles = msg.files?.length ? msg.files : msg.file ? [msg.file] : [];
              const hasAnyFiles = msgFiles.length > 0;

              // ============ GROUP REACTIONS BY EMOJI ============
              // ============ GROUP REACTIONS BY EMOJI AND COUNT ============
              const reactionMap = new Map();

              msgReactions.forEach((reaction: any) => {
                const emoji = reaction.emoji;

                if (!reactionMap.has(emoji)) {
                  reactionMap.set(emoji, {
                    emoji: emoji,
                    count: 0,
                    users: new Set(),
                    userReacted: false,
                    actionTimetoken: reaction.actionTimetoken // Store the most recent for removal
                  });
                }

                const reactionData = reactionMap.get(emoji);
                reactionData.count += 1;
                reactionData.users.add(reaction.userId);

                // Track if current user reacted with this emoji
                if (reaction.userId === currentUserId) {
                  reactionData.userReacted = true;
                  reactionData.actionTimetoken = reaction.actionTimetoken; // Store for removal
                }
              });

              // Convert Map to array for rendering
              const reactionGroups = Array.from(reactionMap.values());

              // Date separator
              const prevMsg = visibleMessages[idx - 1];
              const msgDate = new Date(msg.timestamp ?? Date.now());
              const prevDate = prevMsg ? new Date(prevMsg.timestamp ?? 0) : null;
              const showDateSeparator = !prevDate || !isSameDay(msgDate, prevDate);

              // Show sender name only if first msg from this user in a row (and not mine)
              // Also show sender name after a system message (like "X created the group")
              const prevIsSystem = visibleMessages[idx - 1]?.type === "system";
              const showSenderName =
                !isMine &&
                msg.type !== "system" &&
                !msg.isDeleted &&
                (idx === 0 || visibleMessages[idx - 1]?.senderId !== msg.senderId || showDateSeparator || prevIsSystem);

              return (
                <React.Fragment key={msg.id}>
                  {/* Date separator pill */}
                  {showDateSeparator && (
                    <div data-date-label={getDateLabel(msgDate.getTime())} className={`flex justify-center my-3 transition-opacity duration-300 ${isFloatingDateVisible && floatingDateLabel === getDateLabel(msgDate.getTime()) ? "opacity-0" : "opacity-100"}`}>
                      <span className="text-[11px] text-slate-500 bg-white px-3 py-1 rounded-full shadow-sm">
                        {getDateLabel(msgDate.getTime())}
                      </span>
                    </div>
                  )}

                  {/* System message — centered pill (WhatsApp style) */}
                  {(msg.type === "system") ? (
                    <div className="flex justify-center my-1">
                      <span className="text-[11.5px] text-slate-500 bg-white/80 px-3 py-1 rounded-lg shadow-sm max-w-[85%] text-center">
                        {msg.text}
                      </span>
                    </div>
                  ) : (
                    <div
                      data-message-id={msg.id}
                      className={`flex ${isMine ? "justify-end" : "justify-start"} ${highlightedMessageId === msg.id ? "animate-highlight-msg" : ""}`}
                      ref={(el) => {
                        if (el) messageRefs.current.set(msg.id, el);
                        else messageRefs.current.delete(msg.id);
                      }}
                    >
                      <div
                        className={`relative group max-w-[75%] ${isMine ? "order-1" : ""} ${reactionGroups.length > 0 ? "mb-4" : ""
                          }`}
                      >
                        {/* Action buttons on hover — hidden when removed from group */}
                        {!msg.isDeleted && !isRemoved && !readOnly && (
                          <div
                            className={`absolute top-1 opacity-0 group-hover:opacity-100 transition-all duration-200 flex gap-0.5 z-10 ${isMine ? "left-0 -translate-x-full pr-1" : "right-0 translate-x-full pl-1"
                              }`}
                          >
                            <button
                              onClick={() => handleReplyToMessage(msg)}
                              className="p-1.5 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors"
                              title="Reply"
                            >
                              <Lucide icon="Reply" className="w-3.5 h-3.5 text-[#54656f]" />
                            </button>
                            {isMine && Date.now() - msg.timestamp < 60_000 && (
                              <button
                                onClick={(e) => handleShowActionMenu(e, msg)}
                                className="p-1.5 rounded-full bg-white shadow-md hover:bg-gray-100 transition-colors"
                                title="More options"
                              >
                                <Lucide icon="ChevronDown" className="w-3.5 h-3.5 text-[#54656f]" />
                              </button>
                            )}
                          </div>
                        )}

                        {/* Message Bubble */}
                        <div
                          className={`relative px-[9px] pt-[5px] pb-[7px] text-[13.5px] leading-[19px] shadow-sm overflow-hidden ${msg.isDeleted
                            ? "bg-white/80 italic text-[#8696a0] rounded-lg"
                            : isMine
                              ? "text-white rounded-tl-lg rounded-tr-lg rounded-bl-lg rounded-br-none"
                              : "bg-white text-[#111b21] rounded-tl-none rounded-tr-lg rounded-br-lg rounded-bl-lg"
                            }`}
                          style={{
                            ...(isMine && !msg.isDeleted && {
                              background: "linear-gradient(15deg, #4C1D95 15%, #9F1239 90%)",
                              opacity: '75%',
                            }),
                          }}
                          onContextMenu={(e) => {
                            if (canReact && !isRemoved) handleMessageContextMenu(e, messageTimetoken);
                          }}
                          onDoubleClick={() => !msg.isDeleted && !isRemoved && handleReplyToMessage(msg)}
                        >
                          {/* Tail */}
                          {!msg.isDeleted && (
                            <div
                              className={`absolute top-0 w-3 h-3 ${isMine ? "right-0 translate-x-1/2" : "left-0 -translate-x-1/2 bg-white"
                                }`}
                              style={{
                                clipPath: isMine
                                  ? "polygon(0 0, 100% 0, 0 100%)"
                                  : "polygon(100% 0, 0 0, 100% 100%)",
                              }}
                            />
                          )}

                          {/* Deleted */}
                          {msg.isDeleted ? (
                            <div className="flex items-center gap-2 py-0.5">
                              <Lucide icon="Ban" className="w-4 h-4" />
                              <span className="text-[13px]">This message was deleted</span>
                            </div>
                          ) : (
                            <>
                              {/* Sender name in group (not mine) */}
                              {showSenderName && (
                                <div
                                  className="text-[11px] font-semibold mb-0.5"
                                  style={{ color: getSenderColor(msg.senderId) }}
                                >
                                  {msg.senderName}
                                </div>
                              )}

                              {/* Reply preview — click to scroll to original */}
                              {msg.replyTo && (
                                <div
                                  className={`mb-1.5 p-2 rounded-md border-l-4 text-[12px] cursor-pointer hover:opacity-90 transition-opacity ${isMine
                                    ? "bg-white/20 border-white/60"
                                    : "bg-[#f0f0f0] border-[#06cf9c]"
                                    }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(msg.replyTo!.messageId, msg.replyTo?.timetoken);
                                  }}
                                >
                                  <div
                                    className={`font-semibold mb-0.5 ${isMine ? "text-white/90" : "text-[#06cf9c]"}`}
                                  >
                                    {msg.replyTo.senderId === currentUserId ? "You" : msg.replyTo.senderName}
                                  </div>
                                  <div className={`line-clamp-1 ${isMine ? "text-white/70" : "text-[#667781]"}`}>
                                    {msg.replyTo.text}
                                  </div>
                                </div>
                              )}

                              {/* File attachments — lazy loaded on click */}
                              {hasAnyFiles && (
                                <div className="mb-2 space-y-1.5">
                                  {msgFiles.map((f: any) => (
                                    <LazyFileAttachment
                                      key={`${msg.id}-${f.id}`}
                                      pubnub={pubnub}
                                      channelId={msg.channelId}
                                      file={f}
                                      messageId={msg.id}
                                      isMine={isMine}
                                    />
                                  ))}
                                </div>
                              )}

                              <div className={msg.text ? "whitespace-pre-wrap break-words" : ""}>
                                {msg.text && (
                                  <>
                                    <span>
                                      {shouldTruncate && !isExpanded ? msg.text.slice(0, 100) + "..." : msg.text}
                                    </span>
                                    {shouldTruncate && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleExpandMessage(String(msg.id));
                                        }}
                                        className={`ml-2 align-baseline text-[12px] font-medium focus:outline-none ${isMine ? "text-white/90" : "text-blue-600"
                                          }`}
                                      >
                                        {isExpanded ? "Read less" : "Read more"}
                                      </button>
                                    )}
                                  </>
                                )}

                                {/* Timestamp + status */}
                                <span className="inline-flex items-center gap-1 float-right ml-2 relative top-[2px]">
                                  <span
                                    className={`text-[10px] ${isMine ? "text-white/70" : "text-[#667781]"}`}
                                  >
                                    {formatTime(msg.timestamp)}
                                  </span>
                                  {msg.isEdited && (
                                    <span
                                      className={`text-[10px] italic ${isMine ? "text-white/70" : "text-[#667781]"}`}
                                    >
                                      edited
                                    </span>
                                  )}
                                  {isMine && <MessageTicks status={getMessageStatus(msg)} />}
                                </span>
                                <div className="clear-both" />
                              </div>
                            </>
                          )}
                        </div>

                        {/* Reactions */}
                        {reactionGroups.length > 0 && !msg.isDeleted && (
                          <div className={`absolute z-10 ${isMine ? "right-2" : "left-2"}`}>
                            <div className="flex items-center gap-0.5 bg-white rounded-full px-1.5 py-0.5 shadow-md border border-gray-100">
                              {reactionGroups.map((group) => {
                                // Resolve user IDs to names for tooltip
                                const userNames: string[] = [];
                                group.users.forEach((uid: string) => {
                                  if (uid === currentUserId) {
                                    userNames.unshift("You");
                                  } else {
                                    const member = members.find((m: ChatParticipant) => m.id === uid);
                                    userNames.push(member?.name || "Unknown");
                                  }
                                });

                                return (
                                  <span
                                    key={group.emoji}
                                    className={`relative flex items-center gap-0.5 px-1 py-0.5 rounded-full cursor-pointer hover:bg-gray-100 transition-all duration-200 group/reaction
                                             ${group.userReacted ? 'bg-purple-50 ring-1 ring-purple-300' : ''} `}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (group.userReacted && !readOnly) {
                                        removeReaction(messageTimetoken, group.actionTimetoken);
                                      } else if(!readOnly) {
                                        addReaction(messageTimetoken, group.emoji);
                                      }
                                    }}
                                  >
                                    <span className="text-[14px]">{group.emoji}</span>
                                    <span className="text-[11px] font-medium text-gray-600">
                                      {group.count}
                                    </span>

                                    {/* WhatsApp-style tooltip with user names */}
                                    <div className={`absolute bottom-full mb-1.5 pointer-events-none opacity-0 group-hover/reaction:opacity-100 transition-opacity duration-200 z-50 ${isMine ? "right-0" : "left-0"}`}>
                                      <div className="bg-[#1D293D] text-white text-[11px] rounded-lg px-2.5 py-1.5 shadow-lg whitespace-nowrap max-w-[180px]">
                                        <div className="flex items-center gap-1.5 mb-1">
                                          <span className="text-[16px]">{group.emoji}</span>
                                          <span className="font-medium">{group.count}</span>
                                        </div>
                                        <div className="space-y-0.5">
                                          {userNames.slice(0, 8).map((name, i) => (
                                            <div key={i} className="truncate text-white/90">{name}</div>
                                          ))}
                                          {userNames.length > 8 && (
                                            <div className="text-white/60">and {userNames.length - 8} more...</div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <ScrollToBottomButton
          visible={showScrollToBottom}
          onClick={scrollToBottom}
        />
      </div>

      {/* ---- Input Area / Removed Banner / Read-only Banner ---- */}
      {isRemoved ? (
        /* WhatsApp-style read-only banner when user was removed */
        <div className="border-t bg-[#F0EAEE] px-4 py-3">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Lucide icon="Ban" className="w-4 h-4" />
            <span className="text-sm">You can't send messages to this group because you're no longer a member.</span>
          </div>
        </div>
      ) : readOnly ? (
        /* Read-only banner for Ongoing Chats tab */
        <div className="border-t bg-[#F0EAEE] px-4 py-3">
          <div className="flex items-center justify-center gap-2 text-slate-500">
            <Lucide icon="Eye" className="w-4 h-4" />
            <span className="text-sm">You are viewing this conversation in read-only mode.</span>
          </div>
        </div>
      ) : (
        <div className="border-t bg-[#F7F1F5] p-3">
          {/* Edit preview */}
          {editingMessage && (
            <div className="mb-2 p-3 bg-white rounded-lg border-l-4 border-blue-500 flex items-start gap-2">
              <Lucide icon="Pencil" className="w-4 h-4 text-blue-500 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-blue-600 mb-1">Editing message</div>
                <div className="text-sm text-slate-600 truncate">{editingMessage.text}</div>
              </div>
              <button onClick={cancelEdit} className="text-slate-400 hover:text-red-500 p-1">
                <Lucide icon="X" className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Reply preview */}
          {replyingTo && !editingMessage && (
            <div className="max-w-[300px] mb-2 p-3 bg-white rounded-lg border-l-4 border-purple-500 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-purple-600 mb-1">
                  Replying to {replyingTo.senderId === currentUserId ? "yourself" : replyingTo.senderName}
                </div>
                <div className="text-sm text-slate-600 truncate">{replyingTo.text}</div>
              </div>
              <button onClick={cancelReply} className="text-slate-400 hover:text-red-500 p-1">
                <Lucide icon="X" className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* File preview — multi-file strip */}
          {selectedFiles.length > 0 && (
            <div className="mb-2 p-2 bg-white rounded-lg">
              <div className="flex items-center justify-between mb-1 px-1">
                <span className="text-xs text-slate-500 font-medium">
                  {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""} selected
                  {selectedFiles.length < MAX_FILES_PER_MESSAGE && (
                    <span className="text-slate-400"> · {MAX_FILES_PER_MESSAGE - selectedFiles.length} more allowed</span>
                  )}
                </span>
                <button
                  onClick={() => handleRemoveFile()}
                  className="text-xs text-red-400 hover:text-red-600"
                  disabled={isSending}
                >
                  Clear all
                </button>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 [&::-webkit-scrollbar]:h-[6px]">
                {selectedFiles.map((item) => (
                  <div key={item.id} className="relative flex-shrink-0 w-16 h-16 group/prev">
                    {item.preview && item.file.type.startsWith("image/") ? (
                      <img
                        src={item.preview}
                        alt="Preview"
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : /* VIDEO UPLOAD DISABLED: video preview thumbnail commented out */
                    /* item.preview && item.file.type.startsWith("video/") ? (
                      <video
                        src={item.preview}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : */ (
                        <div className="w-16 h-16 bg-purple-50 rounded flex flex-col items-center justify-center">
                          <Lucide icon={getFileIcon(item.file.type) as any} className="w-5 h-5 text-purple-600" />
                          <span className="text-[9px] text-slate-500 mt-0.5 truncate max-w-[56px] text-center">
                            {item.file.name.split(".").pop()?.toUpperCase()}
                          </span>
                        </div>
                      )}
                    <button
                      onClick={() => handleRemoveFile(item.id)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/prev:opacity-100 transition-opacity"
                      disabled={isSending}
                    >
                      <Lucide icon="X" className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <ChatMessageInput
            value={inputMessage}
            onChange={handleInputChange}
            onSend={handleSendWithFile}
            onEditSave={handleSaveEdit}
            onCancelEdit={cancelEdit}
            editing={!!editingMessage}
            selectedFiles={selectedFiles.map((f) => f.file)}
            onFileSelect={handleFileSelect}
            onEmojiSelect={handleEmojiSelect}
            allowedFileTypes={ALLOWED_FILE_TYPES}
          />

        </div>
      )}

      <MessageActionMenu
        isOpen={actionMenuState.isOpen}
        position={actionMenuState.position}
        message={actionMenuState.message}
        handleStartEdit={handleStartEdit}
        handleShowDeleteModal={handleShowDeleteModal}
        closeActionMenu={() => setActionMenuState({ isOpen: false, message: null, position: { x: 0, y: 0 } })}
      />

      <ActionModal
        isOpen={deleteModal.isOpen}
        title="Delete message?"
        onClose={() => setDeleteModal({ isOpen: false, message: null })}
        actions={[
          {
            label: "Delete for me",
            description: "Only you won't see this message",
            icon: "User",
            onClick: () => handleDelete(false),
          },
          {
            label: "Delete for everyone",
            description: "This message will be deleted for all",
            icon: "Users",
            variant: "danger",
            onClick: () => handleDelete(true),
          },
        ]}
      />


      {/* ---- Reaction Picker ---- */}
      {!readOnly && <MessageReactionPicker
        isOpen={reactionPickerState.isOpen}
        position={reactionPickerState.position}
        onReactionSelect={handleAddReaction}
        onClose={closeReactionPicker}
      />}
    </div>
  );
};

export default GroupChatPanel;
